import React, { useState, useEffect, useCallback } from 'react';
import { registrationAPI, Salt } from '../../services/registrationApi';
import StructureImage from '../../components/chemistry/StructureImage';
import RDKitStructure from '../../components/chemistry/RDKitStructure';

interface Rules {
  org_code: string;
  stereo_matters: boolean;
  salt_creates_version: boolean;
  hydrate_creates_version: boolean;
  solvate_creates_version: boolean;
  polymorph_creates_version: boolean;
  tautomer_match: boolean;
  auto_register: boolean;
  mw_min: number | null;
  mw_max: number | null;
  logp_min: number | null;
  logp_max: number | null;
  formal_charge_allowed: boolean;
  must_have_structure: boolean;
  must_have_name: boolean;
  reject_invalid_valence: boolean;
  salt_strip_policy: string;
  tautomer_policy: string;
  stereo_policy: string;
  // Structured fields
  ez_isomers_matter: boolean;
  rs_chirality_matter: boolean;
  hydrate_solvate_creates_version: boolean;
  cocrystal_creates_version: boolean;
  // Pipeline policies
  normalise_groups: boolean;
  neutralise_policy: string;
  disconnect_metals: boolean;
  aromatise_policy: string;
  promote_role: string;
}

const DEFAULTS: Rules = {
  org_code: 'DIS',
  stereo_matters: true,
  salt_creates_version: true,
  hydrate_creates_version: true,
  solvate_creates_version: true,
  polymorph_creates_version: true,
  tautomer_match: true,
  auto_register: true,
  mw_min: null,
  mw_max: 1000,
  logp_min: null,
  logp_max: null,
  formal_charge_allowed: true,
  must_have_structure: true,
  must_have_name: false,
  reject_invalid_valence: true,
  salt_strip_policy: 'strip',
  tautomer_policy: 'canonicalise',
  stereo_policy: 'preserve',
  ez_isomers_matter: true,
  rs_chirality_matter: true,
  hydrate_solvate_creates_version: true,
  cocrystal_creates_version: true,
  normalise_groups: true,
  neutralise_policy: 'neutralise',
  disconnect_metals: true,
  aromatise_policy: 'aromatise',
  promote_role: 'admin',
};

const Toggle: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void; hint?: string }> = ({ label, checked, onChange, hint }) => (
  <label className="flex items-center justify-between py-2.5 cursor-pointer">
    <div className="pr-4">
      <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{label}</span>
      {hint && <div className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--text-muted)' }}>{hint}</div>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors"
      style={{ backgroundColor: checked ? '#10b981' : 'var(--card-border)' }}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  </label>
);

const RadioOption: React.FC<{ label: string; hint: string; selected: boolean; onSelect: () => void }> = ({ label, hint, selected, onSelect }) => (
  <label
    className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors"
    style={{
      backgroundColor: selected ? 'rgba(16,185,129,0.06)' : 'transparent',
      border: `1px solid ${selected ? '#10b981' : 'var(--card-border)'}`,
    }}
    onClick={onSelect}
  >
    <div className="mt-0.5 flex-shrink-0">
      <div
        className="w-4 h-4 rounded-full border-2 flex items-center justify-center"
        style={{ borderColor: selected ? '#10b981' : 'var(--card-border)' }}
      >
        {selected && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#10b981' }} />}
      </div>
    </div>
    <div>
      <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{label}</span>
      <div className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--text-muted)' }}>{hint}</div>
    </div>
  </label>
);

const NumInput: React.FC<{ label: string; value: number | null; onChange: (v: number | null) => void; placeholder?: string }> = ({ label, value, onChange, placeholder }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{label}</span>
    <input
      type="number"
      step="any"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
      placeholder={placeholder || 'none'}
      className="w-28 px-2 py-1 text-sm rounded border"
      style={{ color: 'var(--text-primary)', backgroundColor: 'var(--content-bg)', borderColor: 'var(--card-border)' }}
    />
  </div>
);

const Section: React.FC<{ title: string; number: number; children: React.ReactNode }> = ({ title, number, children }) => (
  <div className="mb-6">
    <div className="flex items-center gap-2 mb-3">
      <span
        className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
        style={{ backgroundColor: 'var(--hover-bg)', color: 'var(--text-muted)' }}
      >
        {number}
      </span>
      <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{title}</h3>
    </div>
    <div className="rounded-lg border p-4" style={{ borderColor: 'var(--card-border)', background: 'var(--card-bg)' }}>
      {children}
    </div>
  </div>
);

const RegistrationRulesPage: React.FC = () => {
  const [rules, setRules] = useState<Rules>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const [salts, setSalts] = useState<Salt[]>([]);
  const [saltsLoading, setSaltsLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    registrationAPI.getRules()
      .then((res) => {
        const d = res.data;
        if (d && (d as any).tenant_id) {
          setRules({ ...DEFAULTS, ...d });
        } else if ((d as any)?.rules) {
          setRules({ ...DEFAULTS, ...(d as any).rules });
        }
      })
      .catch((err) => console.error('Failed to load rules:', err))
      .finally(() => setLoading(false));

    registrationAPI.getSaltDictionary()
      .then((r) => setSalts(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setSaltsLoading(false));
  }, []);

  // Auto-save: update a single field immediately
  const update = useCallback(<K extends keyof Rules>(key: K, value: Rules[K]) => {
    setRules((prev) => ({ ...prev, [key]: value }));
    setToast('Saving...');
    registrationAPI.updateRules({ [key]: value } as any)
      .then((res) => {
        if (res.data && (res.data as any).tenant_id) {
          setRules(prev => ({ ...prev, ...res.data }));
        }
        setToast('Saved');
        setTimeout(() => setToast(null), 1500);
      })
      .catch(() => {
        setToast('Failed to save');
        setTimeout(() => setToast(null), 2000);
      });
  }, []);

  if (loading) return <div className="p-6 text-sm" style={{ color: 'var(--text-muted)' }}>Loading rules...</div>;

  return (
    <div className="p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Registration Rules</h2>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Configure how compounds are validated and registered. Changes save automatically.</p>
        </div>
      </div>

      {toast && (
        <div
          className="mb-4 px-3 py-2 rounded-lg text-sm"
          style={{
            backgroundColor: toast.includes('Failed') ? '#fef2f2' : '#ecfdf5',
            color: toast.includes('Failed') ? '#991b1b' : '#065f46',
            border: `1px solid ${toast.includes('Failed') ? '#fecaca' : '#a7f3d0'}`,
          }}
        >
          {toast}
        </div>
      )}

      {/* Organisation — always visible */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Organisation</h3>
        </div>
        <div className="rounded-lg border p-4" style={{ borderColor: 'var(--card-border)', background: 'var(--card-bg)' }}>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Org Code</span>
            <input
              type="text"
              maxLength={3}
              value={rules.org_code}
              onChange={(e) => update('org_code', e.target.value.toUpperCase())}
              className="w-20 px-2 py-1 text-sm rounded border font-mono text-center"
              style={{ color: 'var(--text-primary)', backgroundColor: 'var(--content-bg)', borderColor: 'var(--card-border)' }}
            />
          </div>
        </div>
      </div>

      {/* SECTION 1: Tautomer Policy */}
      <Section title="Tautomer Policy" number={1}>
        <div className="space-y-2">
          <RadioOption
            label="Canonicalise"
            hint="Convert all tautomers to a single canonical form for uniqueness matching"
            selected={rules.tautomer_policy === 'canonicalise'}
            onSelect={() => update('tautomer_policy', 'canonicalise')}
          />
          <RadioOption
            label="Preserve as drawn"
            hint="Keep the exact form the user drew or imported"
            selected={rules.tautomer_policy === 'preserve' || rules.tautomer_policy === 'preserve_drawn'}
            onSelect={() => update('tautomer_policy', 'preserve')}
          />
          <RadioOption
            label="Physiological mixture"
            hint="Store as a tautomeric mixture at physiological pH"
            selected={rules.tautomer_policy === 'physiological_mixture'}
            onSelect={() => update('tautomer_policy', 'physiological_mixture')}
          />
        </div>
      </Section>

      {/* SECTION 2: Stereochemistry Policy */}
      <Section title="Stereochemistry Policy" number={2}>
        <Toggle
          label="E/Z isomers are different registrations"
          checked={rules.ez_isomers_matter}
          onChange={(v) => update('ez_isomers_matter', v)}
          hint="Cis/trans geometric isomers around double bonds are separate compounds"
        />
        <div style={{ borderTop: '1px solid var(--card-border)', margin: '4px 0' }} />
        <Toggle
          label="R/S chirality matters"
          checked={rules.rs_chirality_matter}
          onChange={(v) => update('rs_chirality_matter', v)}
          hint="R and S enantiomers at chiral centres are separate compounds"
        />
      </Section>

      {/* SECTION 3: Salt Handling */}
      <Section title="Salt Handling" number={3}>
        <Toggle
          label="Strip salt on import"
          checked={rules.salt_strip_policy === 'strip'}
          onChange={(v) => update('salt_strip_policy', v ? 'strip' : 'keep')}
          hint="Separate the salt/counterion from the parent structure. The salt is kept as associated data — never discarded."
        />
      </Section>

      {/* SECTION 3b: Pipeline Processing */}
      <Section title="Pipeline Processing" number={0}>
        <Toggle
          label="Normalise functional groups"
          checked={rules.normalise_groups}
          onChange={(v) => update('normalise_groups', v)}
          hint="Standardise nitro, sulfo, and other functional group representations"
        />
        <div style={{ borderTop: '1px solid var(--card-border)', margin: '4px 0' }} />
        <Toggle
          label="Disconnect metals"
          checked={rules.disconnect_metals}
          onChange={(v) => update('disconnect_metals', v)}
          hint="Break metal-ligand bonds for organometallic compounds"
        />
        <div style={{ borderTop: '1px solid var(--card-border)', margin: '8px 0' }} />
        <div className="flex items-center justify-between py-2">
          <div>
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Charge handling</span>
            <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>How to handle charged species</div>
          </div>
          <select
            value={rules.neutralise_policy}
            onChange={(e) => update('neutralise_policy', e.target.value)}
            className="px-2 py-1 text-sm rounded border"
            style={{ color: 'var(--text-primary)', backgroundColor: 'var(--content-bg)', borderColor: 'var(--card-border)' }}
          >
            <option value="neutralise">Neutralise</option>
            <option value="reionise">Reionise</option>
            <option value="preserve">Preserve charges</option>
          </select>
        </div>
        <div style={{ borderTop: '1px solid var(--card-border)', margin: '4px 0' }} />
        <div className="flex items-center justify-between py-2">
          <div>
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Aromaticity</span>
            <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Ring notation style</div>
          </div>
          <select
            value={rules.aromatise_policy}
            onChange={(e) => update('aromatise_policy', e.target.value)}
            className="px-2 py-1 text-sm rounded border"
            style={{ color: 'var(--text-primary)', backgroundColor: 'var(--content-bg)', borderColor: 'var(--card-border)' }}
          >
            <option value="aromatise">Aromatise</option>
            <option value="kekulise">Kekulise</option>
            <option value="preserve">Preserve as drawn</option>
          </select>
        </div>
        <div style={{ borderTop: '1px solid var(--card-border)', margin: '8px 0' }} />
        <div className="flex items-center justify-between py-2">
          <div>
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Promotion role</span>
            <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Minimum role to promote compounds to development</div>
          </div>
          <select
            value={rules.promote_role}
            onChange={(e) => update('promote_role', e.target.value)}
            className="px-2 py-1 text-sm rounded border"
            style={{ color: 'var(--text-primary)', backgroundColor: 'var(--content-bg)', borderColor: 'var(--card-border)' }}
          >
            <option value="member">Any member</option>
            <option value="admin">Administrator</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
      </Section>

      {/* SECTION 4: Version Creation */}
      <Section title="Version Creation" number={4}>
        <Toggle
          label="Different salt = new version"
          checked={rules.salt_creates_version}
          onChange={(v) => update('salt_creates_version', v)}
        />
        <div style={{ borderTop: '1px solid var(--card-border)', margin: '4px 0' }} />
        <Toggle
          label="Different hydrate/solvate = new version"
          checked={rules.hydrate_solvate_creates_version}
          onChange={(v) => update('hydrate_solvate_creates_version', v)}
        />
        <div style={{ borderTop: '1px solid var(--card-border)', margin: '4px 0' }} />
        <Toggle
          label="Different polymorph = new version"
          checked={rules.polymorph_creates_version}
          onChange={(v) => update('polymorph_creates_version', v)}
        />
        <div style={{ borderTop: '1px solid var(--card-border)', margin: '4px 0' }} />
        <Toggle
          label="Different cocrystal = new version"
          checked={rules.cocrystal_creates_version}
          onChange={(v) => update('cocrystal_creates_version', v)}
        />
      </Section>

      {/* SECTION 5: Property Ranges & Validation */}
      <Section title="Property Ranges" number={5}>
        <Toggle
          label="Reject invalid valence"
          checked={rules.reject_invalid_valence}
          onChange={(v) => update('reject_invalid_valence', v)}
          hint="Block 5-valent carbon, etc."
        />
        <div style={{ borderTop: '1px solid var(--card-border)', margin: '4px 0' }} />
        <Toggle
          label="Auto-register clean structures"
          checked={rules.auto_register}
          onChange={(v) => update('auto_register', v)}
          hint="Automatically register compounds that pass all validation rules"
        />
        <div style={{ borderTop: '1px solid var(--card-border)', margin: '4px 0' }} />
        <Toggle
          label="Must have structure"
          checked={rules.must_have_structure}
          onChange={(v) => update('must_have_structure', v)}
        />
        <div style={{ borderTop: '1px solid var(--card-border)', margin: '4px 0' }} />
        <Toggle
          label="Allow formal charges"
          checked={rules.formal_charge_allowed}
          onChange={(v) => update('formal_charge_allowed', v)}
          hint="Charged species, zwitterions"
        />
        <div style={{ borderTop: '1px solid var(--card-border)', margin: '8px 0' }} />
        <NumInput label="MW min" value={rules.mw_min} onChange={(v) => update('mw_min', v)} placeholder="no min" />
        <NumInput label="MW max" value={rules.mw_max} onChange={(v) => update('mw_max', v)} placeholder="no max" />
        <NumInput label="LogP min" value={rules.logp_min} onChange={(v) => update('logp_min', v)} placeholder="no min" />
        <NumInput label="LogP max" value={rules.logp_max} onChange={(v) => update('logp_max', v)} placeholder="no max" />
      </Section>

      {/* SECTION 6: Salt Dictionary */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: 'var(--hover-bg)', color: 'var(--text-muted)' }}>6</span>
          <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Salt Dictionary</h3>
        </div>
        <div className="rounded-lg border p-4" style={{ borderColor: 'var(--card-border)', background: 'var(--card-bg)' }}>
          {saltsLoading ? (
            <div className="flex items-center gap-2 py-2">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" />
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Loading salts...</span>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {salts.length} FDA-approved pharmaceutical counterions.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    const name = prompt('Salt name:');
                    if (!name) return;
                    const smiles = prompt('SMILES:') || '';
                    const formula = prompt('Formula:') || '';
                    const charge = parseInt(prompt('Charge (e.g. -1, 1):') || '0', 10);
                    registrationAPI.addSalt({ name, smiles, formula, charge, category: 'Custom' })
                      .then(() => {
                        setToast('Salt added');
                        setTimeout(() => setToast(null), 1500);
                        registrationAPI.getSaltDictionary().then(r => setSalts(Array.isArray(r.data) ? r.data : []));
                      })
                      .catch(() => { setToast('Failed to add salt'); setTimeout(() => setToast(null), 2000); });
                  }}
                  className="text-xs px-2 py-1 rounded border"
                  style={{ color: '#0d9488', borderColor: '#0d9488', backgroundColor: 'transparent' }}
                >
                  + Add Custom Salt
                </button>
              </div>
              {(() => {
                const categoryOrder = [
                  'Hydrohalic acid', 'Inorganic acid', 'Sulfonic acid',
                  'Carboxylic acid', 'Dicarboxylic acid', 'Tricarboxylic acid',
                  'Amino acid', 'Fatty acid',
                  'Alkali metal', 'Alkaline earth', 'Transition metal', 'Metal',
                  'Organic base', 'Ammonium', 'Solvate', 'Custom',
                ];
                const grouped: Record<string, Salt[]> = {};
                for (const s of salts) {
                  const cat = s.category || 'Other';
                  if (!grouped[cat]) grouped[cat] = [];
                  grouped[cat].push(s);
                }
                const cats = categoryOrder.filter(c => grouped[c]);
                for (const c of Object.keys(grouped)) {
                  if (!cats.includes(c)) cats.push(c);
                }

                const toggleSalt = (saltId: number, enabled: boolean) => {
                  setSalts(prev => prev.map(s => s.salt_id === saltId ? { ...s, enabled } : s));
                  registrationAPI.updateSalt(saltId, { enabled })
                    .then(() => { setToast('Saved'); setTimeout(() => setToast(null), 1500); })
                    .catch(() => {
                      setSalts(prev => prev.map(s => s.salt_id === saltId ? { ...s, enabled: !enabled } : s));
                      setToast('Failed to save');
                      setTimeout(() => setToast(null), 2000);
                    });
                };

                return cats.map(cat => {
                  const items = grouped[cat];
                  const isOpen = expandedCategories.has(cat);
                  return (
                    <div key={cat} className="mb-0.5">
                      <button
                        type="button"
                        onClick={() => setExpandedCategories(prev => {
                          const next = new Set(prev);
                          if (next.has(cat)) next.delete(cat); else next.add(cat);
                          return next;
                        })}
                        className="w-full flex items-center justify-between py-1.5 px-2 rounded text-left"
                        style={{ backgroundColor: isOpen ? 'var(--hover-bg)' : 'transparent' }}
                      >
                        <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {cat}
                          <span className="ml-1.5 font-normal" style={{ color: 'var(--text-muted)' }}>({items.length})</span>
                        </span>
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{isOpen ? '\u25B2' : '\u25BC'}</span>
                      </button>
                      {isOpen && (
                        <table className="w-full mt-0.5 mb-2" style={{ fontSize: '0.7rem' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                              <th className="text-center px-1 py-1 w-10" style={{ color: 'var(--text-muted)' }}>On</th>
                              <th className="text-left px-1 py-1 w-12" style={{ color: 'var(--text-muted)' }}>Ion</th>
                              <th className="text-left px-1 py-1" style={{ color: 'var(--text-muted)' }}>Name</th>
                              <th className="text-left px-1 py-1" style={{ color: 'var(--text-muted)' }}>Formula</th>
                              <th className="text-right px-1 py-1 w-10" style={{ color: 'var(--text-muted)' }}>Chg</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map(s => (
                              <tr
                                key={s.salt_id}
                                style={{
                                  borderBottom: '1px solid var(--card-border)',
                                  opacity: s.enabled ? 1 : 0.4,
                                  height: 36,
                                }}
                                title={`SMILES: ${s.smiles || 'N/A'}`}
                              >
                                <td className="text-center px-1">
                                  <button
                                    type="button"
                                    onClick={() => toggleSalt(s.salt_id, !s.enabled)}
                                    className="inline-flex items-center justify-center w-7 h-4 rounded-full"
                                    style={{ backgroundColor: s.enabled ? '#10b981' : 'var(--card-border)' }}
                                  >
                                    <span className={`inline-block w-3 h-3 rounded-full bg-white transition-transform ${s.enabled ? 'translate-x-1.5' : '-translate-x-1.5'}`} />
                                  </button>
                                </td>
                                <td className="px-1" style={{ width: 50, height: 36 }}>
                                  {s.is_ion ? (
                                    <span style={{
                                      fontSize: 14, fontWeight: 600,
                                      color: s.charge != null && s.charge > 0 ? '#2563eb' : s.charge != null && s.charge < 0 ? '#dc2626' : 'var(--text-primary)',
                                    }}>
                                      {s.formula || s.name}
                                    </span>
                                  ) : (
                                    <RDKitStructure smiles={s.smiles} width={40} height={36} />
                                  )}
                                </td>
                                <td className="px-1" style={{ color: 'var(--text-primary)' }}>{s.name}</td>
                                <td className="px-1" style={{ color: 'var(--text-secondary)' }}>{s.formula || '\u2014'}</td>
                                <td className="text-right px-1 font-mono" style={{ color: 'var(--text-secondary)' }}>
                                  {s.charge != null ? (s.charge > 0 ? `+${s.charge}` : s.charge) : ''}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                });
              })()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegistrationRulesPage;
